import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface SessionUser {
  id?: string;
  email?: string | null;
  name?: string | null;
  role?: string;
}

export interface Session {
  user?: SessionUser;
  expires?: string;
}

interface AuthContextValue {
  data: Session | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  update: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  data: null,
  status: 'loading',
  update: async () => {},
});

let refreshSession: (() => Promise<void>) | null = null;

function bindSessionRefresh(fn: () => Promise<void>) {
  refreshSession = fn;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  const update = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/session', { credentials: 'include' });
      const data = await res.json();
      if (data?.user) {
        setSession(data);
        setStatus('authenticated');
      } else {
        setSession(null);
        setStatus('unauthenticated');
      }
    } catch {
      setSession(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    bindSessionRefresh(update);
    return () => {
      bindSessionRefresh(async () => {});
    };
  }, [update]);

  useEffect(() => {
    void update();
  }, [update]);

  const value = useMemo(
    () => ({ data: session, status, update }),
    [session, status, update]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useSession() {
  const ctx = useContext(AuthContext);
  return { data: ctx.data, status: ctx.status, update: ctx.update };
}

export async function signIn(
  provider: string,
  options?: {
    email?: string;
    password?: string;
    redirect?: boolean;
    callbackUrl?: string;
  }
) {
  if (provider !== 'credentials') {
    return { error: 'Unsupported provider' };
  }
  const res = await fetch('/api/auth/callback/credentials', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: options?.email, password: options?.password }),
  });
  if (!res.ok) {
    return { error: 'CredentialsSignin' };
  }
  await refreshSession?.();
  if (options?.redirect !== false && typeof window !== 'undefined') {
    window.location.assign(options?.callbackUrl || '/admin');
  }
  return { error: null };
}

export async function signOut(options?: { callbackUrl?: string }) {
  await fetch('/api/auth/signout', { method: 'POST', credentials: 'include' });
  if (typeof window !== 'undefined') {
    window.location.assign(options?.callbackUrl || '/');
  }
}
